<template>
    <div
        :id="id"
        :class="[classes, `fileuploader ${showDropzone ? '': 'hide-dropzone'}`]"
    >
        <div v-for="file in files" :id="file.id" :key="file.id" class="file-progress" v-if="status(file) !== 'complete'">
            <div class="vtk-container"></div>
            <div class="current-file">
                <h3>
                    {{file.name}}
                    <a v-if="canCancel(file)" href="javascript: void 0" @click="cancelFile(file)"><i class="fas fa-times"></i></a>
                </h3>
            </div>
            <div class="progress">
                <div :class="progressClass(file)" :style="`width: ${progressPerc(file)}`"></div>
            </div>
            <div class="status-msg">{{$t(message(file))}}</div>
            <div class="stl-preview" v-if="file.previews.length > 0">
                <img v-for="preview in file.previews" :src="preview.url" :alt="preview.name"/>
            </div>
        </div>
        <div
            :class="`dropzone ${showDefaultMsg ? '' : 'hide-msg'}`"
            :style="`min-height: ${height || 200}px; padding: ${height ? (height - 20)/2 + 'px' : '2em'} 0`"
        ></div>
    </div>
</template>

<script>
  import Util from '../util.js'
  import Dropzone from '../dropzone.js'
  import * as CryptoJS from 'crypto-js'
  import 'vtk.js'

  const CAMERAS = {
    i: [0.7,0.7,0.7],
    x: [1.1,0,0],
    y: [0,1.1,0],
    z: [0,0,1.1],
  };

  const VIEWS = {
    i: [0,0,1],
    x: [0,0,1],
    y: [0,0,1],
    z: [0,1,0],
  }

  const MIN_DISTANCE = 0.5;
  const CAM_ANGLE = 10;

  const calculateDistance = (len1, len2, min_distance, cam_angle) => {
    const len = Math.max(len1, len2)
    const dist = len / 2.0 / Math.tan(cam_angle * Math.PI / 180 / 2.0)
    return Math.max(dist, (len / 2.0 + min_distance));
  }

  export default {
    props: {
      name: {
        type: String,
        default: ''
      },
      url: {
        type: String,
      },
      paramName: {
        type: String,
        default: 'file'
      },
      maxFilesize: {
        type: Number,
        default: 2
      },
      maxFiles: {
        type: Number,
        default: 1
      },
      acceptedFiles: {
        type: [String, Array],
        default: ''
      },
      showDefaultMsg: {
        type: Boolean,
        default: true
      },
      appendToken: {
        type: Boolean,
        default: false
      },
      classes: {
        type: Object,
        default: () => ({transparent: false})
      },
      progressbar: {
        type: Boolean,
        default: false
      },
      colorClass: {
        type: String,
        default: 'success'
      },
      placeholder: {
        type: [String, Object]
      },
      fileNames: {
        type: String,
        default: null,
      },
      errorMessage: {
        type: [String, Object],
        default: null,
      },
      height: {
        type: Number,
      }
    },
    data () {
      return {
        files: [],
      }
    },
    computed: {
      id: function () {
        return Util.uuid();
      },
      showDropzone () {
        return this.files.length === 0;
      },
      placeholderMessage () {
        return this.$t(this.placeholder || 'File.messageUpload');
      },
      dropzoneAcceptedFiles() {
        let dropzoneAcceptedFiles = '';
        if (Array.isArray(this.acceptedFiles)) {
          for (let k = 0; k < this.acceptedFiles.length; k++) {
            if (k > 0) {
              dropzoneAcceptedFiles += ',';
            }
            dropzoneAcceptedFiles += (this.acceptedFiles[k].startsWith('.') ? '' : '.') + this.acceptedFiles[k];
          }
        } else if (this.acceptedFiles) {
          dropzoneAcceptedFiles = (this.acceptedFiles.startsWith('.') ? '' : '.') + this.acceptedFiles;
        }

        return dropzoneAcceptedFiles;
      }
    },
    methods: {

      status(file) {
        if (file.cancelled) {
          return 'cancelled';
        }

        if (file.preparing && !file.prepared) {
          return 'preparing';
        }

        if (file.sending && !file.sent) {
          return 'sending';
        }

        if (file.processing && !file.processed) {
          return 'processing';
        }

        if (file.sent) {
          return 'complete';
        }

        return 'idle';
      },

      canCancel(file) {
        const status = this.status(file);
        return status === 'idle' || status === 'preparing' || status === 'sending';
      },

      cancelFile(file) {
        if (file.sending) {
          file.xhr.abort();
        }

        file.cancelled = true;

        this.$forceUpdate();

        this.$emit('status', 'cancelled', file);
      },

      progressPerc (file) {
        return this.status(file) === 'sending' ? file.progress + '%' : '100%';
      },
      progressClass (file) {
        var c = ['progress-bar', 'progress-bar-' + this.colorClass];
        if (this.status(file) === 'preparing' || this.status(file) === 'processing') {
          c.push('progress-bar-striped');
          c.push('active');
        }
        return c;
      },
      message (file) {
        switch (this.status(file)) {
          case 'cancelled':
            return 'File.cancelled';
          case 'preparing':
            return 'File.preparingData';
          case 'sending':
            return 'File.sendingData';
          case 'processing':
            return 'File.processingData';
          case 'error':
            return 'File.errorUpload';
          case 'idle':
            return 'File.waiting';
          default:
            return '';
        }
      },

      async renderPreview(file, tasks) {
        file.processing = true;
        this.$forceUpdate();

        const reader = vtk.IO.Geometry.vtkSTLReader.newInstance();
        const mapper = vtk.Rendering.Core.vtkMapper.newInstance({ scalarVisibility: false });
        const actor = vtk.Rendering.Core.vtkActor.newInstance();

        mapper.setInputConnection(reader.getOutputPort());

        actor.setMapper(mapper);

        actor.getProperty().setAmbient(0.45)
        actor.getProperty().setAmbientColor(0.0, 0.65, 0.87)
        actor.getProperty().setDiffuse(0.7)
        actor.getProperty().setDiffuseColor(0.0, 0.65, 0.87)
        actor.getProperty().setInterpolationToPhong()
        actor.getProperty().setSpecular(0.25)
        actor.getProperty().setSpecularColor(0.5, 0.7, 0.8)
        actor.getProperty().setSpecularPower(30)

        const fileReader = new FileReader();

        await new Promise(r => {
          fileReader.onload = r;
          fileReader.readAsArrayBuffer(file);
        })

        reader.parseAsArrayBuffer(fileReader.result);

        const fullScreenRenderer = vtk.Rendering.Misc.vtkFullScreenRenderWindow.newInstance({
          container: document.querySelector(`#${file.id} .vtk-container`),
        });
        const renderer = fullScreenRenderer.getRenderer();
        const renderWindow = fullScreenRenderer.getRenderWindow();

        renderer.addActor(actor);
        renderer.setBackground(1,1,1);

        renderer.resetCamera();

        const [min_x, max_x, min_y, max_y, min_z, max_z] = renderer.computeVisiblePropBounds();
        const len_x = Math.abs(max_x - min_x);
        const len_y = Math.abs(max_y - min_y);
        const len_z = Math.abs(max_z - min_z);

        const dist_x = calculateDistance(len_y, len_z, MIN_DISTANCE, CAM_ANGLE)
        const dist_y = calculateDistance(len_x, len_z, MIN_DISTANCE, CAM_ANGLE)
        const dist_z = calculateDistance(len_x, len_y, MIN_DISTANCE, CAM_ANGLE)

        const dev_x = (max_x + min_x) / 2;
        const dev_y = (max_y + min_y) / 2;
        const dev_z = (max_z + min_z) / 2;

        file.blobs = {};

        for(let key of Object.keys(CAMERAS)) {
          const camera = vtk.Rendering.Core.vtkCamera.newInstance();

          const [loc_x, loc_y, loc_z] = CAMERAS[key];
          const cam_x = dev_x + dist_x * loc_x;
          const cam_y = dev_y + dist_y * loc_y;
          const cam_z = dev_z + dist_z * loc_z;

          camera.setPosition(cam_x, cam_y, cam_z);
          camera.setFocalPoint(dev_x, dev_y, dev_z);
          camera.setViewAngle(CAM_ANGLE);
          camera.roll(-90);
          camera.setViewUp(...VIEWS[key]);

          renderer.setActiveCamera(camera);

          console.time('renderWindow.render()');
          renderWindow.render();
          console.timeEnd('renderWindow.render()');

          const [imagePromise] = renderWindow.captureImages();
          const base64 = await imagePromise;

          file.previews.push({
            url: base64,
            name: `${key}.png`
          });
          this.$forceUpdate();

          await new Promise(r => setTimeout(r, 100));

          file.blobs[key] = await (await fetch(base64)).blob();
        }

        tasks.unshift(
          { task: 'upload-preview', file, tag: file.hash }
        );
      },

      async upload(file, key, blob, onReady, onProgress) {
        const {body} = await this.$http.post(key, {});
        const {url, fields} = body.result;

        const formData = new FormData;

        Object.entries(fields)
          .forEach(([key, value]) => formData.append(key, value));

        formData.append('file', blob);

        return new Promise( (resolve, reject)=>{
          const xhr = new XMLHttpRequest();

          xhr.open('POST', url);
          xhr.onload = ({target}) => resolve(target.responseText);
          xhr.onerror = reject;
          if (xhr.upload && onProgress) {
            xhr.upload.onprogress = ({loaded, total}) => {
              onProgress(file, loaded / total * 100, loaded);
            }
          }

          onReady && onReady(file, xhr, formData);

          xhr.send(formData);

          const original = xhr.abort;
          xhr.abort = () => {
            original.call(xhr);
            resolve(null);
          }
          file.xhr = xhr;
        });
      },

      async uploadFile(file, tasks) {
        let key = `${this.url}/${file.hash}/${file.hash}.${file.name.split('.').pop()}?name=${file.name}`;

        const handlers = {
          ready: (file, xhr, formData) => {
            file.sending = true;
            this.$forceUpdate();
            this.$emit('status', 'sending', file);
            this.$emit('sending', this, file, xhr, formData);
          },

          progress: (file, progress, sent) => {
            file.progress = progress;
            this.$forceUpdate();
            this.$emit('progress', progress, file);
          }
        };

        await this.upload(file, key, file, handlers.ready, handlers.progress);

        file.sent = true;
        this.$emit('progress', 100, file);
        this.$emit('status', 'complete', file);
      },

      async uploadPreview(file) {
        if (file.blobs) {
          await Promise.all(Object
            .entries(file.blobs)
            .map(([camera, blob]) => {
              const key = `${this.url}/${file.hash}/${file.hash}-${camera}.png`;
              return this.upload(file, key, blob);
            }));
        }

        file.processed = true;
        this.$forceUpdate();
      },

      async errorFiles(files, msg) {
        while (files.length) {
          console.error('status', 'error', files.shift(), this.$t(msg));
          this.$emit('status', 'error', files.shift(), this.$t(msg));
        }
      },

      async addedFiles(files) {
        files = [...files];
        const maxFiles = this.maxFiles != null ? this.maxFiles : Number.MAX_SAFE_INTEGER;
        if (files.length > maxFiles) {
          this.errorFiles(files, 'errorFileCountLimit');
        } else if (files.some(file => file.size > this.maxFilesize * 1024 * 1024)) {
          this.errorFiles(files, 'errorFileSizeLimit');
        } else {
          const fileNames = this.fileNames && new RegExp(this.fileNames, 'i');
          if (fileNames && !files.every(({name}) => fileNames.test(name))) {
            this.errorFiles(files, this.errorMessage || 'errorFileNameInvalid');
          }
        }

        const tasks = [];
        try {
          for(let i = 0; i < files.length; ++i) {
            const file = files[i];

            file.id = Util.uuid();


            this.$emit('addfile', this, file);

            file.progress = 0;
            file.previews = [];

            this.files.push(file);

            tasks.push(
              { task: 'hash', file, exclusive: true, tag: file.hash},
            );
          }

          this.$forceUpdate();

          await this.processTasks(tasks, 3, async ({task, file}) => {
            if (file.cancelled) {
              return ;
            }
            switch (task) {
              case 'hash':
                return this.calculateHash(file, tasks);
              case 'upload-file':
                return this.uploadFile(file, tasks);
              case 'preview':
                return this.renderPreview(file, tasks);
              case 'upload-preview':
                return this.uploadPreview(file, tasks);
            }
          });

          this.files.forEach(file => this.$emit('complete', this, file));
          this.files = [];
          this.$forceUpdate();

          this.$emit('status', 'idle');
        } catch (e) {
          console.error(e);

          this.$emit('status', 'error', e.message || e);
        }
      },

      async calculateHash(file, tasks) {
        file.preparing = true;
        this.$forceUpdate();

        const chunkSize  = 1024*1024; // bytes

        let offset       = 0;
        let size         = chunkSize;
        let index        = 0;
        const hash = CryptoJS.algo.SHA1.create();

        while (offset < file.size) {
          const partial = file.slice(offset, offset + size);
          const data = await new Promise(resolve => {
            const reader = new FileReader;
            reader.onload = ({target}) => resolve(target.result);
            reader.readAsArrayBuffer(partial);
          })

          hash.update(CryptoJS.lib.WordArray.create(data));

          offset += chunkSize;
          index += 1;
        }

        file.hash = hash.finalize().toString();
        file.prepared = true;
        this.$forceUpdate();

        tasks.push({ task: 'upload-file', file, tag: file.hash });
        if (file.name.split('.').pop().toLowerCase() === 'stl') {
          tasks.push({task: 'preview', file, tag: file.hash, exclusive: true});
        }
      },

      async processTasks(tasks, concurency, handler) {
        return new Promise((resolve, reject) => {
          let activeCount = 0;
          const activeKinds = {};
          const pickTask = () => {
            console.log('pickTask', tasks, activeCount, activeKinds);

            if (tasks.length < 1 && activeCount < 1) {
              return resolve();
            }

            if (activeCount >= concurency) {
              return;
            }

            for (let i = 0; i < tasks.length; ++i) {
              const {exclusive, task} = tasks[i];
              if (!exclusive || !activeKinds[task]) {
                activeCount++;
                console.log('picked', tasks[i]);
                handler(...tasks.splice(i, 1))
                  .then(() => activeCount--)
                  .then(pickTask)
                  .catch(console.error);
              }
            }
          }

          pickTask();
        });
      },
    },
    mounted () {

      const addedFiles = this.addedFiles.bind(this);
      const $emit = this.$emit.bind(this);

      new Dropzone(`#${this.id} .dropzone`, {
        maxFilesize: this.maxFilesize,

        uploadMultiple: false,
        parallelUploads: 1,
        acceptedFiles: this.dropzoneAcceptedFiles,
        url: 'javascript: void 0',

        dictDefaultMessage: this.placeholderMessage,

        addedfiles(files) {
          this.removeAllFiles(true);
          addedFiles([...files]);
        },

        init () {
          this.on('error', console.error);
          $emit('status', 'idle');
        }
      });
    },
  };

</script>
<style>
  .fileuploader .dz-preview {
    display: none;
  }
</style>
